import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Menu, QrCode } from "lucide-react";

export default function Header() {
  const { pathname } = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsLoggedIn(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const isActive = (path: string) => pathname === path;

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="group flex items-center gap-2 transition-all duration-300 hover:scale-105">
          <QrCode className="h-6 w-6 text-foreground transition-colors duration-300 group-hover:text-primary" />
          <span className="text-xl font-bold text-foreground transition-colors duration-300 group-hover:text-primary">
            MorphQR
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors hover:text-primary ${
              isActive("/") ? "text-primary" : "text-muted-foreground"
            }`}
          >
            Home
          </Link>
          <Link
            to="/blog"
            className={`text-sm font-medium transition-colors hover:text-primary ${
              isActive("/blog") || pathname.startsWith("/blog/")
                ? "text-primary"
                : "text-muted-foreground"
            }`}
          >
            Blog
          </Link>
          <Link
            to="/about"
            className={`text-sm font-medium transition-colors hover:text-primary ${
              isActive("/about") ? "text-primary" : "text-muted-foreground"
            }`}
          >
            About
          </Link>
          <Link
            to="/contact"
            className={`text-sm font-medium transition-colors hover:text-primary ${
              isActive("/contact") ? "text-primary" : "text-muted-foreground"
            }`}
          >
            Contact
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {isLoggedIn ? (
            <Link to="/dashboard">
              <Button>Dashboard</Button>
            </Link>
          ) : (
            <>
              <Link to="/auth">
                <Button variant="outline">Login</Button>
              </Link>
              <Link to="/auth">
                <Button>Sign Up</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" className="h-9 w-9">
              <Menu className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-64 p-6">
            <nav className="flex flex-col gap-3 mt-6">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className={`text-sm font-medium transition-colors hover:text-primary px-3 py-2 rounded-md hover:bg-muted ${
                  isActive("/") ? "text-primary bg-muted" : "text-muted-foreground"
                }`}
              >
                Home
              </Link>
              <Link
                to="/blog"
                onClick={closeMobileMenu}
                className={`text-sm font-medium transition-colors hover:text-primary px-3 py-2 rounded-md hover:bg-muted ${
                  isActive("/blog") || pathname.startsWith("/blog/")
                    ? "text-primary bg-muted"
                    : "text-muted-foreground"
                }`}
              >
                Blog
              </Link>
              <Link
                to="/about"
                onClick={closeMobileMenu}
                className={`text-sm font-medium transition-colors hover:text-primary px-3 py-2 rounded-md hover:bg-muted ${
                  isActive("/about") ? "text-primary bg-muted" : "text-muted-foreground"
                }`}
              >
                About
              </Link>
              <Link
                to="/contact"
                onClick={closeMobileMenu}
                className={`text-sm font-medium transition-colors hover:text-primary px-3 py-2 rounded-md hover:bg-muted ${
                  isActive("/contact") ? "text-primary bg-muted" : "text-muted-foreground"
                }`}
              >
                Contact
              </Link>

              <div className="border-t border-border mt-3 pt-3 flex flex-col gap-2">
                {isLoggedIn ? (
                  <Link to="/dashboard" onClick={closeMobileMenu}>
                    <Button size="sm" className="w-full">Dashboard</Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/auth" onClick={closeMobileMenu}>
                      <Button size="sm" variant="outline" className="w-full">Login</Button>
                    </Link>
                    <Link to="/auth" onClick={closeMobileMenu}>
                      <Button size="sm" className="w-full">Sign Up</Button>
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
